import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { useModuleStatus } from '@/hooks/useModuleStatus.ts'
import { BetaBadge } from '@/components/ui/BetaBadge.tsx'
import { Modal } from '@/components/ui'
import { SapSyncSection } from './sections/SapSyncSection.tsx'
import {
  Database, Search,
  Package, Sparkles,
  Loader2,
  RefreshCw, AlertTriangle, CheckCircle2, ArrowRightLeft,
  Wand2, Settings, TrendingUp, FileText, PieChart, CalendarX2, ShoppingCart,
  ChevronDown, Ruler, GitMerge, ShieldCheck, Book,
} from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import type { ArticleSyncConflict, SubPage } from './shared.ts'
import { ArticleListSection } from './sections/ArticleListSection.tsx'
import { ConflictsSection } from './sections/ConflictsSection.tsx'
import { MissingArticlesPanel } from './sections/ArticleGroupsSection.tsx'
import { AiTextsSection } from './sections/AiTextsSection.tsx'
import { ContentStandardSection } from './sections/ContentStandardSection.tsx'
import { EgisSearchSection } from './sections/EgisSearchSection.tsx'
import { TextAdjustmentSection } from './sections/TextAdjustmentSection.tsx'
import { MarginAnalysisSection } from './sections/MarginAnalysisSection.tsx'
import { EolTrackingSection } from './sections/EolTrackingSection.tsx'
import { SupplierPriceSection } from './sections/SupplierPriceSection.tsx'
import { InventoryTurnoverSection } from './sections/InventoryTurnoverSection.tsx'
import { UnitsSection } from './sections/UnitsSection.tsx'
import { ArtikelAgentModal } from './ArtikelAgentModal.tsx'
import { AiVerificationSection } from './sections/AiVerificationSection.tsx'
import { ServicekatalogSection } from './sections/ServicekatalogSection.tsx'

// ─── Main Page ──────────────────────────────────────────────────────────────

export function ArtikelPage() {
  const { isBeta } = useModuleStatus('artikel')
  const [subPage, setSubPage] = useState<SubPage>('articles')
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Counts for tab badges
  const { data: conflicts = [] } = useQuery<ArticleSyncConflict[]>({
    queryKey: ['article-conflicts'],
    queryFn: () => apiClient.get('/articles/conflicts').then((r) => r.data),
    retry: false,
  })

  const { data: articleCount } = useQuery<number>({
    queryKey: ['articles-count'],
    queryFn: () => apiClient.get('/articles', { params: { page: 1, pageSize: 1 } }).then((r) => r.data.total),
    retry: false,
  })

  interface ArticleStatsOverview {
    totalLocal: number
    perTenant: { tenantCode: string; tenantName: string; count: number }[]
  }

  const { data: statsOverview } = useQuery<ArticleStatsOverview>({
    queryKey: ['articles-stats-overview'],
    queryFn: () => apiClient.get('/articles/stats/overview').then((r) => r.data),
    retry: false,
  })


  interface SyncResult {
    tenantCode: string
    created: number
    updated: number
    conflicts: number
    errors: string[]
    totalErrors: number
  }
  interface SyncSummary {
    results: SyncResult[]
    warning?: string
    error?: string
  }
  interface SyncTenantProgress {
    tenantCode: string
    processed: number
    created: number
    updated: number
    errors: number
    done: boolean
  }
  interface SyncStatus {
    running: boolean
    startedAt?: string
    finishedAt?: string
    lastResult?: SyncSummary
    processed?: number
    created?: number
    updated?: number
    errors?: number
    tenants?: SyncTenantProgress[]
  }

  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [agentOpen, setAgentOpen] = useState(false)
  const dropdownBarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownBarRef.current && !dropdownBarRef.current.contains(e.target as Node)) {
        setOpenDropdown(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null)
  const [syncPolling, setSyncPolling] = useState(false)
  const [sapSyncModalOpen, setSapSyncModalOpen] = useState(false)
  const lastProcessedRef = useRef<number>(0)

  interface PushTenantProgress {
    tenantId: string
    tenantCode: string
    pushed: number
    failed: number
  }
  interface PushStatus {
    running: boolean
    startedAt?: string
    finishedAt?: string
    pushed?: number
    failed?: number
    skipped?: number
    total?: number
    error?: string
    tenants?: PushTenantProgress[]
    errors?: string[]
  }
  const [pushStatus, setPushStatus] = useState<PushStatus | null>(null)
  const [pushPolling, setPushPolling] = useState(false)

  // Poll sync status while running
  const { data: polledStatus } = useQuery<SyncStatus>({
    queryKey: ['sync-status'],
    queryFn: () => apiClient.get('/articles/sync/status').then((r) => r.data),
    refetchInterval: syncPolling ? 2000 : false,
    enabled: syncPolling,
  })

  // React to poll results via useEffect (not inline during render)
  useEffect(() => {
    if (!polledStatus || !syncPolling) return

    const currentProcessed = polledStatus.processed ?? 0
    if (currentProcessed > lastProcessedRef.current) {
      lastProcessedRef.current = currentProcessed
      queryClient.invalidateQueries({ queryKey: ['articles'] })
      queryClient.invalidateQueries({ queryKey: ['articles-count'] })
    }

    if (!polledStatus.running) {
      setSyncPolling(false)
      setSyncStatus(polledStatus)
      lastProcessedRef.current = 0
      queryClient.invalidateQueries({ queryKey: ['articles'] })
      queryClient.invalidateQueries({ queryKey: ['articles-count'] })
      queryClient.invalidateQueries({ queryKey: ['article-groups'] })
      queryClient.invalidateQueries({ queryKey: ['article-groups-count'] })
      queryClient.invalidateQueries({ queryKey: ['article-conflicts'] })
      queryClient.invalidateQueries({ queryKey: ['articles-stats-overview'] })
    }
  }, [polledStatus, syncPolling, queryClient])

  const { data: polledPushStatus } = useQuery<PushStatus>({
    queryKey: ['push-missing-status'],
    queryFn: () => apiClient.get('/articles/push-missing/status').then((r) => r.data),
    refetchInterval: pushPolling ? 2000 : false,
    enabled: pushPolling,
  })

  useEffect(() => {
    if (!polledPushStatus || !pushPolling) return
    setPushStatus(polledPushStatus)
    if (!polledPushStatus.running) {
      setPushPolling(false)
      queryClient.invalidateQueries({ queryKey: ['articles-missing-sync'] })
      queryClient.invalidateQueries({ queryKey: ['articles'] })
      queryClient.invalidateQueries({ queryKey: ['articles-count'] })
    }
  }, [polledPushStatus, pushPolling, queryClient])

  const [unitsSyncResult, setUnitsSyncResult] = useState<{ created: number; skipped: number; tenants: Array<{ tenantCode: string; created: number; skipped: number; error?: string }> } | null>(null)

  useMutation<SyncStatus>({
    mutationFn: async () => {
      // Import units from all SAP tenants in parallel with article sync start
      apiClient.post('/articles/units/import-from-all-sap', {}).then((r) => {
        setUnitsSyncResult({ created: r.data.created, skipped: r.data.skipped, tenants: r.data.tenants ?? [] })
        queryClient.invalidateQueries({ queryKey: ['article-units'] })
      }).catch((err) => {
        setUnitsSyncResult({ created: 0, skipped: 0, tenants: [{ tenantCode: '?', created: 0, skipped: 0, error: (err as Error).message }] })
      })
      const res = await apiClient.post('/articles/sync')
      return res.data
    },
    onSuccess: (data) => {
      setSyncStatus(data)
      if (data.running) {
        setSyncPolling(true)
      }
    },
  })

  const directTabs: { key: SubPage; label: string; icon: typeof Database; badge?: number; badgeColor?: string }[] = [
    { key: 'articles', label: 'Artikel', icon: Package, badge: articleCount },
    { key: 'service-catalog', label: 'Servicekatalog', icon: Book },
    { key: 'conflicts', label: 'Konflikte', icon: ArrowRightLeft, badge: conflicts.length },
    { key: 'missing-sync', label: 'Fehlende Artikel', icon: GitMerge },
    { key: 'egis', label: 'EGIS-Suche', icon: Search },
    { key: 'supplier-prices', label: 'Lieferantenpreise', icon: ShoppingCart },
  ]

  const dropdownGroups: {
    key: string
    label: string
    icon: typeof Database
    items: { key: SubPage; label: string; icon: typeof Database }[]
  }[] = [
    {
      key: 'stammdaten',
      label: 'Stammdaten',
      icon: Database,
      items: [
        { key: 'units', label: 'Maßeinheiten', icon: Ruler },
      ],
    },
    {
      key: 'texte-ki',
      label: 'Texte & KI',
      icon: Sparkles,
      items: [
        { key: 'ai-verification', label: 'KI-Qualitätsprüfung', icon: ShieldCheck },
        { key: 'ai', label: 'KI-Texte', icon: Sparkles },
        { key: 'content-standard', label: 'Content-Standard', icon: Wand2 },
        { key: 'text-adjustment', label: 'Textanpassung', icon: FileText },
      ],
    },
    {
      key: 'statistiken',
      label: 'Statistiken',
      icon: PieChart,
      items: [
        { key: 'margin-analysis', label: 'Margen-Analyse', icon: PieChart },
        { key: 'eol-tracking', label: 'EOL/EOS-Tracking', icon: CalendarX2 },
        { key: 'inventory-turnover', label: 'Lagerumschlag', icon: TrendingUp },
      ],
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Database className="w-7 h-7 text-brand-500" />
            Artikelverwaltung
            {isBeta && <BetaBadge size="md" />}
          </h1>
          <p className="text-sm text-gray-500 mt-1">Artikelstamm, Preise, Stücklisten, KI-Texte und EGIS-Import</p>
          {statsOverview && (
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                <Database className="w-3 h-3" />
                {statsOverview.totalLocal.toLocaleString('de')} lokal
              </span>
              {statsOverview.perTenant.map((t) => (
                <span key={t.tenantCode} className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand-50 text-brand-700 text-xs font-medium rounded-full">
                  {t.tenantCode}: {t.count.toLocaleString('de')}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAgentOpen(true)}
            className="flex items-center gap-2 px-3 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition-colors"
            title="Artikel per KI aus Text anlegen"
          >
            <Wand2 className="w-4 h-4" />
            KI-Assistent
          </button>
          <button
            onClick={() => navigate('/einstellungen/artikel')}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors"
            title="Artikeleinstellungen (Warengruppen, Preisgruppen, Defaults)"
          >
            <Settings className="w-4 h-4" />
            Einstellungen
          </button>
          <button
            onClick={() => setSapSyncModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            SAP Sync
          </button>
        </div>
      </div>

      {syncPolling && (
        <div className="px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
          <div className="flex items-center gap-2 mb-1.5">
            <Loader2 className="w-4 h-4 animate-spin shrink-0" />
            <span>SAP-Synchronisation läuft im Hintergrund…</span>
          </div>
          {polledStatus?.tenants && polledStatus.tenants.length > 0 ? (
            <div className="ml-6 flex flex-col gap-0.5">
              {polledStatus.tenants.map((t) => (
                <div key={t.tenantCode} className="flex items-center gap-2">
                  {t.done
                    ? <span className="text-green-600 font-medium">✓</span>
                    : <Loader2 className="w-3 h-3 animate-spin" />
                  }
                  <span className="font-medium">{t.tenantCode}</span>
                  <span className="text-blue-600">
                    {t.processed} geladen
                    {t.created > 0 && `, ${t.created} neu`}
                    {t.updated > 0 && `, ${t.updated} aktualisiert`}
                    {t.errors > 0 && <span className="text-red-500">, {t.errors} Fehler</span>}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <span className="ml-6 font-medium">
              {polledStatus?.processed ?? 0} geladen
              {(polledStatus?.created ?? 0) > 0 && `, ${polledStatus!.created} neu`}
              {(polledStatus?.updated ?? 0) > 0 && `, ${polledStatus!.updated} aktualisiert`}
            </span>
          )}
        </div>
      )}

      {!syncPolling && syncStatus?.lastResult && (() => {
        const d = syncStatus.lastResult!
        if (d.error) {
          return (
            <div className="flex items-center gap-2 px-4 py-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertTriangle className="w-4 h-4" />
              Synchronisation fehlgeschlagen: {d.error}
            </div>
          )
        }
        const totalCreated = d.results.reduce((s, r) => s + r.created, 0)
        const totalUpdated = d.results.reduce((s, r) => s + r.updated, 0)
        const totalConflicts = d.results.reduce((s, r) => s + r.conflicts, 0)
        const totalErrors = d.results.reduce((s, r) => s + (r.totalErrors ?? r.errors.length), 0)
        const hasWarning = d.warning || totalErrors > 0
        const displayErrors = d.results.flatMap((r) => r.errors)
        return (
          <div className={cn(
            'rounded-lg text-sm border',
            hasWarning ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-green-50 border-green-200 text-green-700',
          )}>
            <div className="flex items-center gap-2 px-4 py-2.5">
              {hasWarning ? <AlertTriangle className="w-4 h-4 flex-shrink-0" /> : <CheckCircle2 className="w-4 h-4 flex-shrink-0" />}
              <span>
                Synchronisation abgeschlossen: {totalCreated} neu, {totalUpdated} aktualisiert
                {totalConflicts > 0 && `, ${totalConflicts} Konflikte`}
                {totalErrors > 0 && `, ${totalErrors} Fehler`}
                {d.results.length === 0 && ' (keine Artikel gefunden)'}
              </span>
            </div>
            {displayErrors.length > 0 && (
              <div className="px-4 pb-2.5 text-xs space-y-0.5">
                {displayErrors.map((e, i) => <div key={i}>{e}</div>)}
                {totalErrors > displayErrors.length && (
                  <div className="mt-1 text-xs opacity-75">
                    ... und {totalErrors - displayErrors.length} weitere Fehler (siehe Sync-Logs unter Systemeinstellungen)
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })()}

      {pushPolling && (
        <div className="px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
          <div className="flex items-center gap-2 mb-1.5">
            <Loader2 className="w-4 h-4 animate-spin shrink-0" />
            <span>Fehlende Artikel werden in SAP angelegt…</span>
          </div>
          {polledPushStatus?.tenants && polledPushStatus.tenants.length > 0 ? (
            <div className="ml-6 flex flex-col gap-0.5">
              {polledPushStatus.tenants.map((t) => (
                <div key={t.tenantId} className="flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span className="font-medium">{t.tenantCode}</span>
                  <span className="text-blue-600">
                    {t.pushed} angelegt
                    {t.failed > 0 && <span className="text-red-500">, {t.failed} Fehler</span>}
                  </span>
                </div>
              ))}
              <div className="mt-0.5 text-blue-500">
                Gesamt: {polledPushStatus.pushed ?? 0}
                {polledPushStatus.total != null && ` / ${polledPushStatus.total}`}
              </div>
              {polledPushStatus.errors && polledPushStatus.errors.length > 0 && (
                <div className="mt-1.5 text-xs space-y-0.5 text-red-600 font-mono">
                  {polledPushStatus.errors.map((e, i) => <div key={i}>{e}</div>)}
                </div>
              )}
            </div>
          ) : (
            <span className="ml-6 font-medium">
              {polledPushStatus?.pushed ?? 0}
              {polledPushStatus?.total != null && ` / ${polledPushStatus.total}`} gepusht
            </span>
          )}
        </div>
      )}

      {!pushPolling && pushStatus?.finishedAt && (() => {
        if (pushStatus.error) {
          return (
            <div className="flex items-center gap-2 px-4 py-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertTriangle className="w-4 h-4" />
              Push fehlgeschlagen: {pushStatus.error}
            </div>
          )
        }
        const hasFailed = (pushStatus.failed ?? 0) > 0
        return (
          <div className={cn(
            'rounded-lg text-sm border',
            hasFailed ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-green-50 border-green-200 text-green-700',
          )}>
            <div className="flex items-center gap-2 px-4 py-2.5">
              {hasFailed ? <AlertTriangle className="w-4 h-4 flex-shrink-0" /> : <CheckCircle2 className="w-4 h-4 flex-shrink-0" />}
              <span>
                Push abgeschlossen: {pushStatus.pushed ?? 0} angelegt
                {(pushStatus.failed ?? 0) > 0 && `, ${pushStatus.failed} fehlgeschlagen`}
                {(pushStatus.skipped ?? 0) > 0 && `, ${pushStatus.skipped} übersprungen`}
              </span>
            </div>
            {pushStatus.tenants && pushStatus.tenants.length > 0 && (
              <div className="px-4 pb-2 flex flex-col gap-0.5 text-xs">
                {pushStatus.tenants.map((t) => (
                  <div key={t.tenantId} className="flex items-center gap-2">
                    {t.failed > 0 ? <AlertTriangle className="w-3 h-3 text-red-500" /> : <CheckCircle2 className="w-3 h-3 text-green-600" />}
                    <span className="font-medium">{t.tenantCode}:</span>
                    <span>{t.pushed} angelegt{t.failed > 0 && `, ${t.failed} Fehler`}</span>
                  </div>
                ))}
              </div>
            )}
            {pushStatus.errors && pushStatus.errors.length > 0 && (
              <div className="px-4 pb-2.5 text-xs space-y-0.5 opacity-80">
                {pushStatus.errors.map((e, i) => <div key={i} className="font-mono">{e}</div>)}
              </div>
            )}
          </div>
        )
      })()}

      {unitsSyncResult && (() => {
        const errors = unitsSyncResult.tenants.filter(t => t.error)
        const hasErrors = errors.length > 0
        return (
          <div className={cn(
            'px-4 py-2.5 rounded-lg text-sm border',
            hasErrors ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-blue-50 border-blue-200 text-blue-700',
          )}>
            <div className="flex items-center gap-2">
              {hasErrors ? <AlertTriangle className="w-4 h-4 shrink-0" /> : <Ruler className="w-4 h-4 shrink-0" />}
              <span>
                Maßeinheiten: {unitsSyncResult.created} neu importiert, {unitsSyncResult.skipped} bereits vorhanden
                {hasErrors && ` — ${errors.length} Mandant(en) mit Fehler`}
              </span>
            </div>
            {hasErrors && (
              <div className="mt-1 ml-6 space-y-0.5">
                {errors.map((t, i) => (
                  <div key={i} className="text-xs text-red-700">{t.tenantCode}: {t.error}</div>
                ))}
              </div>
            )}
          </div>
        )
      })()}


      <div ref={dropdownBarRef} className="flex gap-1 border-b border-gray-200">
        {directTabs.map(({ key, label, icon: Icon, badge, badgeColor }) => (
          <button
            key={key}
            onClick={() => { setSubPage(key); setOpenDropdown(null) }}
            className={cn(
              'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
              subPage === key ? 'border-brand-500 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700',
            )}
          >
            <Icon className="w-4 h-4" />
            {label}
            {badge != null && (key === 'articles' ? true : badge > 0) && (
              <span className={cn(
                'px-1.5 py-0.5 text-xs font-bold rounded-full',
                badgeColor === 'amber' || key === 'conflicts' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500',
              )}>{badge}</span>
            )}
          </button>
        ))}

        {dropdownGroups.map(({ key, label, icon: Icon, items }) => {
          const isGroupActive = items.some((i) => i.key === subPage)
          const isOpen = openDropdown === key
          return (
            <div key={key} className="relative">
              <button
                onClick={() => setOpenDropdown(isOpen ? null : key)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
                  isGroupActive ? 'border-brand-500 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700',
                )}
              >
                <Icon className="w-4 h-4" />
                {label}
                <ChevronDown className={cn('w-3 h-3 transition-transform', isOpen && 'rotate-180')} />
              </button>
              {isOpen && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[180px] py-1">
                  {items.map(({ key: itemKey, label: itemLabel, icon: ItemIcon }) => (
                    <button
                      key={itemKey}
                      onClick={() => { setSubPage(itemKey); setOpenDropdown(null) }}
                      className={cn(
                        'w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors text-left',
                        subPage === itemKey
                          ? 'bg-brand-50 text-brand-700 font-medium'
                          : 'text-gray-700 hover:bg-gray-50',
                      )}
                    >
                      <ItemIcon className="w-4 h-4" />
                      {itemLabel}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {subPage === 'articles' && <ArticleListSection />}
      {subPage === 'service-catalog' && <ServicekatalogSection />}
      {subPage === 'conflicts' && <ConflictsSection />}
      {subPage === 'missing-sync' && (
        <MissingArticlesPanel
          onPushStarted={(status) => {
            setPushStatus(status)
            if (status.running) setPushPolling(true)
          }}
        />
      )}
      {subPage === 'ai-verification' && <AiVerificationSection />}
      {subPage === 'ai' && <AiTextsSection />}
      {subPage === 'content-standard' && <ContentStandardSection />}
      {subPage === 'egis' && <EgisSearchSection />}
      {subPage === 'text-adjustment' && <TextAdjustmentSection />}
      {subPage === 'margin-analysis' && <MarginAnalysisSection />}
      {subPage === 'eol-tracking' && <EolTrackingSection />}
      {subPage === 'supplier-prices' && <SupplierPriceSection />}
      {subPage === 'inventory-turnover' && <InventoryTurnoverSection />}
      {subPage === 'units' && <UnitsSection />}

      <ArtikelAgentModal
        open={agentOpen}
        onOpenChange={setAgentOpen}
        onArticlesCreated={() => {
          queryClient.invalidateQueries({ queryKey: ['articles'] })
          queryClient.invalidateQueries({ queryKey: ['articles-count'] })
        }}
      />

      <Modal
        open={sapSyncModalOpen}
        onOpenChange={setSapSyncModalOpen}
        title="SAP Sync"
        size="xl"
      >
        <SapSyncSection />
      </Modal>
    </div>
  )
}

